import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TrackerTableDataRow from './TrackerTableDataRow';
import Paper from '@mui/material/Paper';
import TrackerTableHeaderRow from './TrackerTableHeaderRow';
import TablePagination from '@mui/material/TablePagination';
import './Override.css';
import { Box } from '@mui/material';
import { useFilters } from '../TrackerFilter/Context/FiltersContext';
import {
  getFlatContentLogById,
  getFlatEngagementById,
  getFlatIssueById,
  getFlatServiceById,
} from './trackerTableRequest';
import { HttpStatusCode } from 'axios';
import { TrackerDataType, trackerHeaderAccentSoftByType } from '../../shared/constants/constants';
import { IssueAndServiceSharedValues } from '../../shared/constants/IssueAndServiceKeys';
import dayjs from '../../utils/dayjs';

export default function TrackerTable(props) {
  const {
    rows,
    dropdownTable,
    visibleCols,
    eventHandler,
    linkedEventHandler,
    secondaryLinkedEventHandler,
    tertiaryLinkedEventHandler,
    isBulkEdit,
    selectedBulkEditRowIds,
    onBulkEditSelectAll,
    onBulkEditSelectRow,
    fitToContainer = false,
  } = props;
  const [page, setPage] = React.useState(0);
  const [localRowsPerPage, setLocalRowsPerPage] = useState(50);
  const ref = useRef(null);
  const [localSortConfig, setLocalSortConfig] = useState({
    key: IssueAndServiceSharedValues.entryDate,
    direction: 'desc',
  });
  const filters = useFilters();

  // Relevant when rows are filtered by issue/service id since we want to differentiate
  // whether user cant see a post due to post not existing or whether user has no permission
  const [noDataMessage, setNoDataMessage] = useState('');
  const trackerType = Object.values(TrackerDataType).includes(eventHandler?.type)
    ? eventHandler.type
    : TrackerDataType.SERVICE;
  const trackerAccent =
    trackerHeaderAccentSoftByType[trackerType] ?? trackerHeaderAccentSoftByType[TrackerDataType.SERVICE];

  const handleChangePage = (event, newPage) => {
    if (ref.current) {
      ref.current.scrollTop = 0;
    }
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (event) => {
    const nextRowsPerPage = Number.parseInt(event.target.value, 10);
    setLocalRowsPerPage(nextRowsPerPage);
    setPage(0);
  };

  const sortedRows = useMemo(() => {
    if (!Array.isArray(rows)) {
      return [];
    }
    if (!localSortConfig?.key) {
      return rows;
    }

    const dateColumns = new Set([
      IssueAndServiceSharedValues.date,
      IssueAndServiceSharedValues.entryDate,
      IssueAndServiceSharedValues.dueDate,
    ]);

    const getTeamMemberNamesForSort = (value) => {
      if (!value) {
        return '';
      }
      const memberSubs = Array.isArray(value) ? value : [value];
      const leadOptions = dropdownTable?.lead ?? [];
      const leadNameByIdentifier = new Map();
      leadOptions.forEach((lead) => {
        if (lead?.sub) {
          leadNameByIdentifier.set(lead.sub, lead.name ?? '');
        }
      });
      const names = memberSubs
        .map((memberSub) => {
          const name = leadNameByIdentifier.get(memberSub);
          return name ?? '';
        })
        .map((name) => name.trim())
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
      return names.join(', ');
    };

    const normalizeSortValue = (value) => {
      if (value === null || value === undefined) {
        return '';
      }
      if (Array.isArray(value)) {
        return value.map(normalizeSortValue).filter(Boolean).join(', ');
      }
      if (typeof value === 'object') {
        if (value.name) {
          return value.name;
        }
        if (value.label) {
          return value.label;
        }
        if (value.title) {
          return value.title;
        }
        if (value.type && value.name) {
          return `${value.type}:${value.name}`;
        }
        if (value._id) {
          return value._id;
        }
        return String(value);
      }
      return value;
    };

    const compareValues = (aValue, bValue) => {
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return aValue - bValue;
      }
      return String(aValue).localeCompare(String(bValue), undefined, { numeric: true, sensitivity: 'base' });
    };

    return [...rows].sort((aRow, bRow) => {
      const aCell = aRow?.cells?.[localSortConfig.key];
      const bCell = bRow?.cells?.[localSortConfig.key];

      if (localSortConfig.key === IssueAndServiceSharedValues.teamMembers) {
        const aTeamValue = getTeamMemberNamesForSort(aCell);
        const bTeamValue = getTeamMemberNamesForSort(bCell);
        const comparison = aTeamValue.localeCompare(bTeamValue, undefined, {
          numeric: true,
          sensitivity: 'base',
        });
        if (comparison !== 0) {
          return localSortConfig.direction === 'desc' ? -comparison : comparison;
        }
        const tieBreaker = String(aRow?.id ?? '').localeCompare(String(bRow?.id ?? ''), undefined, {
          numeric: true,
          sensitivity: 'base',
        });
        return localSortConfig.direction === 'desc' ? -tieBreaker : tieBreaker;
      }

      let aValue = normalizeSortValue(aCell);
      let bValue = normalizeSortValue(bCell);

      if (dateColumns.has(localSortConfig.key)) {
        const getDateSortValue = (value) => {
          if (!value) {
            return 0;
          }
          const parsed = dayjs(value);
          return parsed.isValid() ? parsed.valueOf() : 0;
        };
        aValue = getDateSortValue(aCell);
        bValue = getDateSortValue(bCell);
      }

      const comparison = compareValues(aValue, bValue);
      return localSortConfig.direction === 'desc' ? -comparison : comparison;
    });
  }, [dropdownTable, localSortConfig, rows]);

  const handleSort = (column) => {
    setLocalSortConfig((prev) => {
      if (prev.key === column) {
        return {
          key: column,
          direction: prev.direction === 'asc' ? 'desc' : 'asc',
        };
      }
      return {
        key: column,
        direction: 'desc',
      };
    });
    setPage(0);
    if (ref.current) {
      ref.current.scrollTop = 0;
    }
  };

  const currentPageRows =
    localRowsPerPage === -1
      ? sortedRows
      : sortedRows.slice(localRowsPerPage * page, localRowsPerPage * page + localRowsPerPage);

  const handleBulkEditSelectAll = (isChecked) => {
    const currentPageRowIds = currentPageRows.map((row) => row.id);
    onBulkEditSelectAll(isChecked, currentPageRowIds);
  };

  const getCurrentPageRowsCount = () => {
    return currentPageRows.length;
  };

  const isBulkEditSelect = (rowId) => selectedBulkEditRowIds.includes(rowId);

  useEffect(() => {
    const checkIfNoDataIsCausedByUserPermissions = async () => {
      const IdFromFilter =
        filters.linkedInteraction?._id ||
        filters.linkedEngagement?._id ||
        (filters.linkedService && filters.linkedService[0]._id) ||
        filters.id;

      if (IdFromFilter && rows.length === 0) {
        try {
          // We expect the get requests to throw an error
          if (eventHandler.type === TrackerDataType.MEDIA_INTERACTION) {
            await getFlatIssueById(IdFromFilter);
          } else if (eventHandler.type === TrackerDataType.ENGAGEMENT) {
            await getFlatEngagementById(IdFromFilter);
          } else if (eventHandler.type === TrackerDataType.CONTENT_LOG) {
            await getFlatContentLogById(IdFromFilter);
          } else {
            await getFlatServiceById(IdFromFilter);
          }
        } catch (err) {
          if (err.response.status === HttpStatusCode.Forbidden) {
            let typeString = 'Service';
            if (eventHandler.type === TrackerDataType.MEDIA_INTERACTION) {
              typeString = 'Media Interaction';
            } else if (eventHandler.type === TrackerDataType.ENGAGEMENT) {
              typeString = 'Engagement';
            } else if (eventHandler.type === TrackerDataType.CONTENT_LOG) {
              typeString = 'Content Log';
            }
            setNoDataMessage(
              `You do not have permission to view this ${typeString}, please contact your company's administrator`
            );
          } else if (err.response.status === HttpStatusCode.NotFound) {
            setNoDataMessage('No data found');
          } else {
            console.error(err);
            setNoDataMessage('Internal Server Error');
          }
        }
      } else {
        setNoDataMessage('No data found');
      }
    };

    checkIfNoDataIsCausedByUserPermissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  return rows.length ? (
    <Box
      sx={
        fitToContainer
          ? {
              height: '100%',
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }
          : undefined
      }
    >
      <TableContainer
        ref={ref}
        component={Paper}
        sx={{
          borderRadius: '10px',
          width: '100%',
          border: '2px solid #B1B9C9',
          borderTop: `2px solid ${trackerAccent}`,
          mx: 'auto',
          maxHeight: fitToContainer ? 'calc(100% - 56px)' : '75vh',
          ...(fitToContainer
            ? {
                flex: '0 1 auto',
                minHeight: 0,
              }
            : null),
          mt: '10px',
          overflow: 'auto',
          scrollbarWidth: 'thin',
          scrollbarColor: '#C5CCD8 transparent',
          '&::-webkit-scrollbar': {
            width: '8px',
            height: '8px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'transparent',
            marginTop: '52px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#C5CCD8',
            borderRadius: '999px',
            border: '2px solid transparent',
            backgroundClip: 'content-box',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            backgroundColor: '#AFB8C7',
          },
        }}
      >
        <Table stickyHeader sx={{ minWidth: 0, m: 0 }} aria-label="simple table">
          <TrackerTableHeaderRow
            columns={visibleCols}
            type={eventHandler.type}
            isBulkEdit={isBulkEdit}
            isBulkEditSelectedAll={selectedBulkEditRowIds.length === getCurrentPageRowsCount()}
            isBulkEditSelectedIndeterminate={
              selectedBulkEditRowIds.length > 0 && selectedBulkEditRowIds.length < getCurrentPageRowsCount()
            }
            onBulkEditSelectAll={handleBulkEditSelectAll}
            sortConfig={localSortConfig}
            onSort={handleSort}
          />
          <TableBody>
            {currentPageRows.map((row, index) => {
              return (
                <TrackerTableDataRow
                  key={row.id}
                  row={row}
                  visibleCols={visibleCols}
                  dropdownTable={dropdownTable}
                  isStripedRow={index % 2}
                  eventHandler={eventHandler}
                  linkedEventHandler={linkedEventHandler}
                  secondaryLinkedEventHandler={secondaryLinkedEventHandler}
                  tertiaryLinkedEventHandler={tertiaryLinkedEventHandler}
                  isBulkEdit={isBulkEdit}
                  isBulkEditSelectedRow={isBulkEditSelect(row.id)}
                  onBulkEditSelectRow={onBulkEditSelectRow}
                />
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[25, 50, 100, { label: 'All', value: -1 }]}
        component="div"
        count={sortedRows.length}
        rowsPerPage={localRowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        sx={{ mr: '2%', flexShrink: 0 }}
        data-cy={'TrackerTable-Pagination'}
      />
    </Box>
  ) : (
    <h2 style={{ marginTop: '1em' }}>{noDataMessage}</h2>
  );
}

TrackerTable.propTypes = {
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      cells: PropTypes.object,
    })
  ).isRequired,
  dropdownTable: PropTypes.shape({
    lead: PropTypes.array,
  }),
  visibleCols: PropTypes.arrayOf(PropTypes.string).isRequired,
  eventHandler: PropTypes.shape({
    type: PropTypes.oneOf([
      TrackerDataType.MEDIA_INTERACTION,
      TrackerDataType.SERVICE,
      TrackerDataType.ENGAGEMENT,
      TrackerDataType.CONTENT_LOG,
    ]).isRequired,
  }).isRequired,
  linkedEventHandler: PropTypes.shape({
    type: PropTypes.oneOf([
      TrackerDataType.MEDIA_INTERACTION,
      TrackerDataType.SERVICE,
      TrackerDataType.ENGAGEMENT,
      TrackerDataType.CONTENT_LOG,
    ]),
    newItemModalTitle: PropTypes.string,
  }),
  secondaryLinkedEventHandler: PropTypes.shape({
    type: PropTypes.oneOf([
      TrackerDataType.MEDIA_INTERACTION,
      TrackerDataType.SERVICE,
      TrackerDataType.ENGAGEMENT,
      TrackerDataType.CONTENT_LOG,
    ]),
    newItemModalTitle: PropTypes.string,
  }),
  tertiaryLinkedEventHandler: PropTypes.shape({
    type: PropTypes.oneOf([
      TrackerDataType.MEDIA_INTERACTION,
      TrackerDataType.SERVICE,
      TrackerDataType.ENGAGEMENT,
      TrackerDataType.CONTENT_LOG,
    ]),
    newItemModalTitle: PropTypes.string,
  }),
  isBulkEdit: PropTypes.bool,
  selectedBulkEditRowIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  onBulkEditSelectAll: PropTypes.func,
  onBulkEditSelectRow: PropTypes.func,
  fitToContainer: PropTypes.bool,
};
